import { html, View } from 'rune-ts';
import { ProductCardView, ProductLikeToggleEvent } from '../ProductCard/ProductCardView.ts';
import { classes } from '../../../../shared/util';
import klass from './ProductCardListView.module.scss';
import {
  ProductCardViewData,
  ProductCardListData,
  ProductCardOption,
} from '../../../../features/ProductList/type';
import { DefaultProductListEmptyScreen } from './ProductCardListEmptyScreen';
import { head, isUndefined, map, pipe, take, toArray, uniqBy } from '@fxts/core';
import { Deque } from '../../../../shared/util/dataStructure/Deque';
import { isProductCardViews } from './utils/isProductCardViews';
import { NewSwiperView } from '../Swiper/NewSwiperView';
import { ProductCardListPluginHelper, ProductCardListPluginProps } from './helpers/plugin-helper.ts';
import { ProductCardItemView } from './views/ProductCardItemView.ts';
import { GlobalTimerManager } from '@services/MarppleShop/shared/util/timer';

type GridOptions = {
  /**
   * @default true
   */
  enabled?: boolean;

  /**
   * columns 와 배타적으로 사용
   */
  rows?: number;

  /**
   * rows 와 배타적으로 사용
   */
  columns?: number;

  column_gap?: number;

  row_gap?: number;
};

type ProductCardListViewData = {
  cardViews: ProductCardView[];
};

export type ProductCardListOption = ProductCardOption & {
  container_klass?: string;

  /**
   * grid 배치 옵션
   * - 이 옵션을 주입하거나 `container_klass`로 스타일을 직접 정의하세요.
   */
  grid?: GridOptions;

  limit?: number;

  plugins?: ProductCardListPluginProps;

  timer?: {
    /**
     * @default true
     */
    enabled?: boolean;
  };
};

export class ProductCardListView extends View<ProductCardListData | ProductCardListViewData> {
  static readonly DEFAULT_VALUES = {
    MO: {
      columns: 2,
      column_gap: 13,
      row_gap: 24,
    },
    PC: {
      columns: 5,
      column_gap: 16,
      row_gap: 32,
    },
  } as const;

  // 상품 목록에 그려진 card views
  private cardViewsDeque: Deque<ProductCardItemView>;

  // prepend 대기 중인 card views
  private readonly prependWaitingDeque = new Deque<ProductCardItemView>();

  // delete 대기 중인 card views
  private readonly deleteWaitingDeque = new Deque<ProductCardItemView>();

  readonly option: ProductCardListOption;

  // 상품 목록이 비어있을 때 보여줄 view
  private emptyListView?: View | null;

  private readonly productListSwiperView?: View;

  private readonly pluginHelper: ProductCardListPluginHelper;

  private readonly cardListController = new ProductCardListViewController(this);

  // card views로 init
  constructor(cardViews: ProductCardView[], option: ProductCardListOption);

  // 상품 data로 init
  constructor(data: ProductCardListData, option: ProductCardListOption);

  constructor(data: ProductCardListData | ProductCardView[], option: ProductCardListOption) {
    const swiperPlugin = ProductCardListPluginHelper.getSwiperPlugin(option.plugins);

    const cardViews = ProductCardListView.generateProductCardViews(
      isProductCardViews(data) ? data : data.products,
      {
        ...option,
        card_klass: swiperPlugin ? klass.swiper_product_card : '',
      },
    );

    super(isProductCardViews(data) ? { cardViews: data } : data, option);

    this.pluginHelper = new ProductCardListPluginHelper(option.plugins);

    this.cardViewsDeque = new Deque(cardViews);
    this.option = option;

    if (swiperPlugin) {
      this.productListSwiperView = new NewSwiperView({
        swiperOptions: {
          ...swiperPlugin.swiperOptions,
          slidesPerView: 'auto',
        },
        views: cardViews,
        styles: {
          swiper: klass.swiper_container,
          wrapper: klass.swiper_wrapper,
        },
      });
    }
  }

  protected override onRender() {
    super.onRender();
    this.delegate(ProductLikeToggleEvent, ProductCardView, async (_, view) => {
      await view.toggleLikeButton();
    });
    this.cardListController.startGlobalTimer();
  }

  protected override onUnmount(): void {
    super.onUnmount();
    this.cardListController.stopGlobalTimer();
  }

  public get productCardViews(): ProductCardItemView[] {
    return this.cardViewsDeque.toArray();
  }

  private generateProductCardViews(items: ProductCardViewData[] | ProductCardView[]): ProductCardItemView[] {
    return ProductCardListView.generateProductCardViews(items, { ...this.option, is_lazy: false });
  }

  public static generateProductCardViews(
    items: ProductCardViewData[] | ProductCardView[],
    option: ProductCardOption & Pick<ProductCardListOption, 'limit'>,
  ): ProductCardItemView[] {
    return pipe(
      items,
      map((item) => {
        if (item instanceof ProductCardView) {
          return new ProductCardItemView({
            cardView: item,
            options: {
              klass: option.klass,
            },
          });
        } else {
          const { klass: itemKlass, ...cardViewOptions } = option;
          const cardView = new ProductCardView(item, cardViewOptions);

          return new ProductCardItemView({
            cardView,
            options: {
              klass: itemKlass,
            },
          });
        }
      }),
      uniqBy((cardListItemView) => cardListItemView.product_id),
      take(option.limit ?? Infinity),
      toArray,
    );
  }

  /**
   * 상품 목록 일괄로 갱신
   */
  public setProductsData(productCardViews: ProductCardView[]): this;
  public setProductsData(products: ProductCardViewData[]): this;
  public setProductsData(items: ProductCardViewData[] | ProductCardView[]): this {
    // 새로운 상품카드 생성하고 추가
    const new_items = this.generateProductCardViews(items);
    this.cardViewsDeque = new Deque(new_items);

    // wait queue 초기화
    this.prependWaitingDeque.clear();
    this.deleteWaitingDeque.clear();

    return this;
  }

  public get isEmpty(): boolean {
    return this.productCardViews.length === 0;
  }

  /**
   * 목록 비어있을 때 view setter
   */
  public setEmptyListView(emptyListView: View | null): this {
    this.emptyListView = emptyListView;
    return this;
  }

  /**
   * 목록 비어있을 때 view getter
   */
  public getEmptyListView(): View | null {
    if (isUndefined(this.emptyListView)) {
      this.emptyListView = new DefaultProductListEmptyScreen();
    }
    return this.emptyListView;
  }

  /**
   * 리스트의 앞 부분에 `ProductCardView` view 추가하는 메서드
   */
  public prepend(cardView: ProductCardView): void {
    const cardListItemView = new ProductCardItemView({ cardView });
    const isMatched = (item: ProductCardItemView) => item.product_id === cardListItemView.product_id;

    // 상품 목록에서 제거
    const deleted = this.cardViewsDeque.delete(isMatched);

    // prepend 대기중이면 제거하고 새 카드 추가
    this.prependWaitingDeque.delete(isMatched);
    this.prependWaitingDeque.unshift(cardListItemView);

    // 삭제된 상품 있으면 delete queue 에 추가
    if (deleted) {
      this.deleteWaitingDeque.unshift(deleted);
    }
  }

  /**
   * queue 데이터와 DOM의 싱크를 맞춰주는 메서드
   */
  public syncCardViews() {
    this.consumePrependWaitingDeque();
    this.truncByLimit();
    this.consumeDeleteWaitingDeque();
  }

  /**
   * `option.limit` 설정되어 있는 경우, limit을 초과하는 상품 카드를 삭제 예약
   */
  private truncByLimit() {
    if (this.option.limit) {
      while (this.cardViewsDeque.size > this.option.limit) {
        const lastCardView = this.cardViewsDeque.pop();
        if (lastCardView) {
          this.deleteWaitingDeque.unshift(lastCardView);
        }
      }
    }
  }

  /**
   * 예약된 prepend 처리
   */
  private consumePrependWaitingDeque(): void {
    const to_prepend_cards: ProductCardItemView[] = [];
    while (this.prependWaitingDeque.size > 0) {
      const lastCardView = this.prependWaitingDeque.pop();
      if (lastCardView) {
        this.cardViewsDeque.unshift(lastCardView);
        to_prepend_cards.push(lastCardView);
      }
    }

    // add to DOM
    this.prependCardViewElements(to_prepend_cards);
  }

  /**
   * 예약된 delete 처리
   */
  private consumeDeleteWaitingDeque(): void {
    while (this.deleteWaitingDeque.size > 0) {
      const lastCardView = this.deleteWaitingDeque.pop();
      if (lastCardView?.element()) {
        // remove to DOM
        lastCardView.element().remove();
      }
    }
  }

  /**
   * 1. `cardViews`를 element 로 변환
   * 2. 변환된 element 목록을 DOM에 prepend
   * 3. fade in
   */
  private prependCardViewElements(cardViews: ProductCardItemView[]): void {
    // create elements
    const card_els = pipe(
      cardViews,
      map((card_view) => card_view.render()),
      toArray,
    );

    // prepend elements
    this.element().prepend(...card_els);

    // fade in
    requestAnimationFrame(() => {
      cardViews.forEach((view) => view.fadeIn());
    });
  }

  public getLastOrderedAt(): Date | null {
    const head_product = head(this.productCardViews);
    return head_product && head_product.ordered_at ? head_product.ordered_at : null;
  }

  private getGridStyle(): string {
    if (!this.option.grid || this.option.grid.enabled === false) {
      return '';
    }

    const { grid } = this.option;
    const styles: string[] = ['display: grid'];

    // grid 배치
    if (grid.rows) {
      styles.push(`grid-template-rows: repeat(${grid.rows}, auto)`);
      styles.push('grid-auto-flow: column');
      styles.push('align-content: start');
    } else {
      styles.push(`grid-template-columns: repeat(${grid.columns}, minmax(0, 1fr))`);
    }

    // grid gap
    if (grid.column_gap) {
      styles.push(`grid-column-gap: ${grid.column_gap}px`);
    }
    if (grid.row_gap) {
      styles.push(`grid-row-gap: ${grid.row_gap}px`);
    }

    return styles.join(';');
  }

  protected override template() {
    const is_empty = !this.productCardViews.length;

    return html`
      <ul
        class="${klass.product_list} ${classes({
          [klass.empty_list]: is_empty,
          [this.option.container_klass!]: this.option.container_klass,
          [klass.hidden]: this.emptyListView === null && is_empty,
        })}"
        style="${this.getGridStyle()};${this.pluginHelper.getSwiperStyleVars()}"
      >
        ${is_empty ? (this.getEmptyListView() ?? '') : (this.productListSwiperView ?? this.productCardViews)}
      </ul>
    `;
  }
}

export class ProductCardListViewController {
  constructor(private readonly cardListView: ProductCardListView) {}

  startGlobalTimer() {
    if (this.isTimerEnabled) {
      const globalTimer = GlobalTimerManager.getInstance();
      globalTimer.start(1000);
    }
  }

  stopGlobalTimer() {
    if (this.isTimerEnabled) {
      const globalTimer = GlobalTimerManager.getInstance();
      globalTimer.start();
    }
  }

  get isTimerEnabled(): boolean {
    return this.cardListView.option.timer?.enabled !== false;
  }
}

export { ProductCardListPlugin } from './helpers/plugin-helper.ts';
